import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import axios from 'axios'
import styled from 'styled-components'
import { supportSchema, type SupportFormInput } from '@src/schemas/supportSchema'
import { contactAPI } from '@src/api'
import {
  WshModal,
  WshModalBackdrop,
  WshModalHeader,
  WshModalBody,
  WshModalFooter,
  WshFormGroup,
  WshFormLabel,
  WshFieldError,
  WshCancelBtn,
  WshSubmitBtn,
} from './WshModalShell'

// ─── Support-specific styled components ───────────────────────────────────────

const WshTextarea = styled.textarea`
  width: 100%;
  min-height: 110px;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.55rem 0.75rem;
  font-size: 16px !important;
  font-family: 'Inter', sans-serif;
  color: #1f2937;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;

  &:focus { border-color: #003366; }
`

const WshCharCount = styled.span<{ $warning?: boolean; $danger?: boolean }>`
  font-size: 0.75rem;
  color: ${p => p.$danger ? '#dc2626' : p.$warning ? '#d97706' : '#9ca3af'};
`

const WshFormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.35rem;
`

// ─── Component ────────────────────────────────────────────────────────────────

interface WshSupportModalProps {
  show: boolean
  onHide: () => void
}

const WshSupportModal: React.FC<WshSupportModalProps> = ({ show, onHide }) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SupportFormInput>({
    resolver: zodResolver(supportSchema),
    defaultValues: { message: '' },
  })

  const messageLength = watch('message')?.length ?? 0

  const handleHide = () => {
    reset()
    onHide()
  }

  const onSubmit = async (data: SupportFormInput) => {
    try {
      await contactAPI.submitSupport({
        category: 'other',
        subject: 'Portal help request',
        message: data.message,
      })
      toast.success('Message sent! We\'ll be in touch.')
      handleHide()
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        toast.error('Too many requests. Please try again later.', { duration: 5000 })
      } else {
        toast.error('Failed to send. Please try again.', { duration: 5000 })
      }
    }
  }

  return (
    <>
      {show && <WshModalBackdrop />}
      <WshModal show={show} onHide={handleHide} centered backdrop={false} keyboard={false}>
        <WshModalHeader closeButton tagline="Contact Support" />
        <WshModalBody>
          <form id="wsh-support-form" onSubmit={handleSubmit(onSubmit)}>
            <WshFormGroup>
              <WshFormLabel>Describe your issue or question</WshFormLabel>
              <WshTextarea
                placeholder="What can we help you with?"
                maxLength={2000}
                rows={5}
                {...register('message')}
              />
              <WshFormFooter>
                <span>{errors.message && <WshFieldError>{errors.message.message}</WshFieldError>}</span>
                <WshCharCount $warning={messageLength > 1600} $danger={messageLength > 1800}>
                  {messageLength}/2000
                </WshCharCount>
              </WshFormFooter>
            </WshFormGroup>
          </form>
        </WshModalBody>
        <WshModalFooter>
          <WshCancelBtn type="button" onClick={handleHide} disabled={isSubmitting}>Cancel</WshCancelBtn>
          <WshSubmitBtn type="submit" form="wsh-support-form" disabled={isSubmitting}>
            {isSubmitting
              ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />Sending...</>
              : <><i className="bi bi-send" />Send</>}
          </WshSubmitBtn>
        </WshModalFooter>
      </WshModal>
    </>
  )
}

export default WshSupportModal
